import { useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import ReactMarkdown from "react-markdown";
import { Bold, Italic, Link as LinkIcon, List, ListOrdered, Heading1, Heading2, Quote, Eye, Pencil } from "lucide-react";

/**
 * Minimal Markdown editor with a toolbar. No new dependencies -- uses react-markdown (already installed).
 * Toolbar buttons wrap the selected text (or insert placeholders) with the appropriate markdown syntax.
 */
export default function MarkdownEditor({ value, onChange, placeholder, minHeight = "10rem" }) {
  const textareaRef = useRef(null);
  const [tab, setTab] = useState("write"); // write | preview

  const wrap = (before, after = before, placeholder = "") => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end) || placeholder;
    const next = value.slice(0, start) + before + selected + after + value.slice(end);
    onChange(next);
    setTimeout(() => {
      el.focus();
      const cursor = start + before.length + selected.length + after.length;
      el.setSelectionRange(cursor, cursor);
    }, 0);
  };

  const prefixLine = (prefix) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    const next = value.slice(0, lineStart) + prefix + value.slice(lineStart);
    onChange(next);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + prefix.length, start + prefix.length);
    }, 0);
  };

  const insertLink = () => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end) || "link text";
    const url = window.prompt("URL:", "https://");
    if (!url) return;
    const md = `[${selected}](${url})`;
    onChange(value.slice(0, start) + md + value.slice(end));
    setTimeout(() => el.focus(), 0);
  };

  const btn = "p-2 rounded hover:bg-slate-200 text-slate-700 disabled:opacity-40";
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b bg-slate-50 flex-wrap">
        <button type="button" className={btn} title="Bold" onClick={() => wrap("**", "**", "bold text")}>
          <Bold className="w-4 h-4" />
        </button>
        <button type="button" className={btn} title="Italic" onClick={() => wrap("*", "*", "italic text")}>
          <Italic className="w-4 h-4" />
        </button>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" className={btn} title="Heading 1" onClick={() => prefixLine("# ")}>
          <Heading1 className="w-4 h-4" />
        </button>
        <button type="button" className={btn} title="Heading 2" onClick={() => prefixLine("## ")}>
          <Heading2 className="w-4 h-4" />
        </button>
        <button type="button" className={btn} title="Quote" onClick={() => prefixLine("> ")}>
          <Quote className="w-4 h-4" />
        </button>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" className={btn} title="Bulleted list" onClick={() => prefixLine("- ")}>
          <List className="w-4 h-4" />
        </button>
        <button type="button" className={btn} title="Numbered list" onClick={() => prefixLine("1. ")}>
          <ListOrdered className="w-4 h-4" />
        </button>
        <button type="button" className={btn} title="Link" onClick={insertLink}>
          <LinkIcon className="w-4 h-4" />
        </button>
        <div className="ml-auto flex gap-1">
          <Button
            type="button"
            size="sm"
            variant={tab === "write" ? "secondary" : "ghost"}
            onClick={() => setTab("write")}
            className="h-7 text-xs"
          >
            <Pencil className="w-3 h-3 mr-1" /> Write
          </Button>
          <Button
            type="button"
            size="sm"
            variant={tab === "preview" ? "secondary" : "ghost"}
            onClick={() => setTab("preview")}
            className="h-7 text-xs"
          >
            <Eye className="w-3 h-3 mr-1" /> Preview
          </Button>
        </div>
      </div>
      {tab === "write" ? (
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{ minHeight }}
          className="border-0 rounded-none focus-visible:ring-0 resize-y font-mono text-sm"
        />
      ) : (
        <div
          className="p-4 prose prose-slate max-w-none prose-sm"
          style={{ minHeight }}
        >
          {value.trim() ? (
            <ReactMarkdown
              components={{
                a: ({ node, ...props }) => (
                  <a {...props} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline" />
                ),
              }}
            >
              {value}
            </ReactMarkdown>
          ) : (
            <p className="text-slate-400 italic">Nothing to preview yet...</p>
          )}
        </div>
      )}
    </div>
  );
}
